import { Plus, Warehouse } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { StockLocation } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useStoreDetail } from '@/features/stores';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeleteLocationDialog } from '../components/delete-location-dialog';
import { useLocationColumns } from '../components/location-columns';
import { LocationDialog } from '../components/location-dialog';
import { locationFilters } from '../components/location-filters';
import { useLocationRowActions, type LocationAction } from '../components/location-row-actions';
import { LowStockAlertsCard } from '../components/low-stock-alerts-card';
import { useUpdateLocation } from '../hooks/use-location-mutations';
import { useStockLocations } from '../hooks/use-stock-locations';

const MAX_LOCATIONS = 20;
const EMPTY: readonly StockLocation[] = [];

type Dialog =
  | { kind: 'none' }
  | { kind: 'edit'; location: StockLocation | null }
  | { kind: 'delete'; location: StockLocation };

function Locations() {
  const { t } = useTranslation('inventory');
  const store = useStore();
  const message = useErrorMessage();
  const query = useStockLocations(store.id);
  const update = useUpdateLocation(store.id);
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const { editable, reason } = useStoreEditable({ permission: 'inventory.write' });
  const { can } = usePermissions();
  const detail = useStoreDetail(store.id, can('settings.read'));
  const locations = query.data ?? EMPTY;
  const full = locations.length >= MAX_LOCATIONS;
  const closeDialog = (open: boolean) => {
    if (!open) setDialog({ kind: 'none' });
  };

  const { mutate: updateLocation } = update;
  const onAction = useCallback(
    (action: LocationAction, location: StockLocation) => {
      switch (action) {
        case 'edit':
          setDialog({ kind: 'edit', location });
          return;
        case 'makeDefault':
          updateLocation(
            { id: location.id, body: { isDefault: true, fulfillsOnlineOrders: true } },
            { onError: (error) => toast.error(message(error)) },
          );
          return;
        case 'delete':
          setDialog({ kind: 'delete', location });
      }
    },
    [message, updateLocation],
  );
  const columns = useLocationColumns();
  const filters = useMemo(() => locationFilters(t), [t]);
  const rowActions = useLocationRowActions(onAction);
  const table = useDataTable({ data: locations, columns, filters, getRowId: (row) => row.id });

  const addButton = (tour?: 'settings.locations.add') =>
    editable ? (
      <Button
        disabled={full}
        onClick={() => setDialog({ kind: 'edit', location: null })}
        data-tour={tour}
      >
        <Plus aria-hidden />
        {t('locations.create.action')}
      </Button>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('locations.title')}
        description={t('locations.description', { store: store.name, max: MAX_LOCATIONS })}
        actions={addButton('settings.locations.add')}
      />
      <ReadOnlyBanner reason={reason === 'closed' ? 'closed' : null} />
      {detail.data ? <LowStockAlertsCard key={detail.data.updatedAt} store={detail.data} /> : null}
      {query.data && locations.length === 0 ? (
        <EmptyState
          icon={Warehouse}
          title={t('locations.empty.title')}
          description={t('locations.empty.description')}
          action={addButton()}
          tour="settings.locations.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          search={{ label: t('locations.search'), placeholder: t('locations.search') }}
          rowActions={editable ? rowActions : undefined}
          tour={{ table: 'settings.locations.table', rowActions: 'settings.locations.row-actions' }}
          getRowLabel={(row) => row.name}
          resourceName={{
            singular: t('locations.resource_one'),
            plural: t('locations.resource_other'),
          }}
          loading={query.isPending}
          fetching={query.isFetching || update.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}
      {dialog.kind === 'edit' ? (
        <LocationDialog
          key={dialog.location?.id ?? 'new'}
          storeId={store.id}
          location={dialog.location}
          open
          onOpenChange={closeDialog}
        />
      ) : null}
      <DeleteLocationDialog
        storeId={store.id}
        location={dialog.kind === 'delete' ? dialog.location : null}
        onOpenChange={closeDialog}
      />
    </PageContent>
  );
}

export function LocationsPage() {
  return (
    <FeatureErrorBoundary name="inventory.locations">
      <Locations />
    </FeatureErrorBoundary>
  );
}
